import { AlertTriangle, CheckCircle2, LockKeyhole, ShieldAlert } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TabularImportPreviewResult } from '../services/tabularImport';

export interface SourceImportReconciliationPanelProps {
  readonly preview: TabularImportPreviewResult;
  readonly ownerImportAccess?: {
    readonly selected: boolean;
    readonly onChange: (selected: boolean) => void;
  };
}

export function SourceImportReconciliationPanel({
  preview,
  ownerImportAccess,
}: SourceImportReconciliationPanelProps): React.ReactElement {
  const { summary } = preview;
  return (
    <div className="space-y-3" data-testid="tid.compass.sourceImportReconciliation">
      <section className="rounded-lg border border-border bg-background p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Review the import</h3>
            <p className="text-xs text-muted-foreground">
              This preview used the same mapping and structural checks as the import. Nothing has been written yet.
            </p>
          </div>
          <span className="rounded-full border border-border bg-muted/50 px-2.5 py-1 text-[11px] text-muted-foreground">
            {summary.sourceRows.toLocaleString()} source rows · {summary.materializedRecords.toLocaleString()} records
          </span>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="New" value={summary.created} tone="success" />
          <Metric label="Changed" value={summary.changed} tone="primary" />
          <Metric label="Unchanged" value={summary.unchanged} tone="neutral" />
          <Metric
            label="Needs review"
            value={summary.unresolved + summary.conflicts}
            tone={summary.unresolved + summary.conflicts > 0 ? 'warning' : 'neutral'}
          />
        </div>

        {summary.protectedFieldCount > 0 ? (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-950">
            <LockKeyhole className="mt-0.5 size-3.5 shrink-0" />
            <span>
              {summary.protectedFieldCount} protected field{summary.protectedFieldCount === 1 ? '' : 's'} will be stored behind the restricted-data boundary. Values are not included in this preview.
              {' '}Existing protected fields are conservatively counted as changed without comparing their values.
            </span>
          </div>
        ) : null}
      </section>

      {preview.reviewBlocking ? (
        <section className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-950">
          <ShieldAlert className="mt-0.5 size-4 shrink-0" />
          <div>
            <div className="font-semibold">Import blocked by a structural conflict</div>
            <div className="mt-0.5">
              Go back to mapping and correct the source structure before importing.
              {preview.preflight?.reasonCodes.length
                ? ` Checks: ${preview.preflight.reasonCodes.map(humanizeReason).join(', ')}.`
                : ''}
            </div>
          </div>
        </section>
      ) : null}

      {summary.conflicts > 0 ? (
        <section className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <div className="font-semibold">
              {summary.conflicts} change{summary.conflicts === 1 ? '' : 's'} conflict with approved repository state
            </div>
            <div className="mt-0.5">
              Approved values remain unchanged. The import can continue and these records will be sent for review.
            </div>
          </div>
        </section>
      ) : null}

      <section className="rounded-lg border border-border bg-background">
        <div className="border-b border-border px-3 py-2.5">
          <div className="text-sm font-semibold text-foreground">Changes by record type</div>
        </div>
        <div className="divide-y divide-border">
          {preview.profiles.map((profile) => (
            <div key={profile.profileId} className="grid gap-2 px-3 py-2.5 md:grid-cols-[160px_1fr]">
              <div>
                <div className="text-sm font-medium text-foreground">{humanizeReason(profile.profileId)}</div>
                <div className="text-[11px] text-muted-foreground">
                  {profile.created} new · {profile.changed} changed · {profile.unchanged} unchanged
                  {profile.conflicts > 0 ? ` · ${profile.conflicts} conflicts` : ''}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {profile.changedFields.slice(0, 12).map((field) => (
                  <span key={field} className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                    {field}
                  </span>
                ))}
                {profile.changedFields.length > 12 ? (
                  <span className="text-[10px] text-muted-foreground">
                    +{profile.changedFields.length - 12} more
                  </span>
                ) : null}
                {profile.changedFields.length === 0 ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                    <CheckCircle2 className="size-3" /> No field changes
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      {preview.issues.length > 0 ? (
        <section className="rounded-lg border border-amber-200 bg-amber-50/40">
          <div className="border-b border-amber-200 px-3 py-2.5 text-sm font-semibold text-amber-950">
            Unresolved rows and relationships
          </div>
          <div className="divide-y divide-amber-200">
            {preview.issues.map((issue) => (
              <div key={`${issue.kind}:${issue.reason}`} className="px-3 py-2.5 text-xs text-amber-950">
                <span className="font-medium">{issue.count} {humanizeReason(issue.kind)} issue{issue.count === 1 ? '' : 's'}:</span>{' '}
                {humanizeReason(issue.reason)}
                {issue.missingFields.length > 0 ? ` · fields: ${issue.missingFields.join(', ')}` : ''}
                {issue.missingEntityRefs.length > 0 ? ` · references: ${issue.missingEntityRefs.join(', ')}` : ''}
              </div>
            ))}
          </div>
          <p className="border-t border-amber-200 px-3 py-2 text-[11px] text-amber-900">
            Safe records can still import. Unresolved facts remain in the folder Sources review queue instead of being guessed.
          </p>
        </section>
      ) : null}

      {ownerImportAccess ? (
        <section
          className="rounded-lg border border-border bg-background p-3"
          data-testid="tid.compass.sourceImportReconciliation.ownerImportAccess"
        >
          <label
            htmlFor="owner-import-access"
            className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground"
          >
            Who needs access?
          </label>
          <Select
            value={ownerImportAccess.selected ? 'owner' : 'unchanged'}
            onValueChange={(value) => ownerImportAccess.onChange(value === 'owner')}
          >
            <SelectTrigger
              id="owner-import-access"
              size="sm"
              className="mt-1.5 w-full sm:max-w-sm"
              aria-label="Who needs Atlas access after this import?"
              data-testid="tid.compass.sourceImportReconciliation.ownerImportAccess.select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unchanged">No access change</SelectItem>
              <SelectItem value="owner">Me — full Atlas access (folder owner)</SelectItem>
            </SelectContent>
          </Select>
          <p className="mt-1.5 max-w-2xl text-[11px] leading-4 text-muted-foreground">
            Selected by default for the importing folder owner. This creates an audited, revocable grant for all Atlas capabilities in this folder, including succession planning, workload, scenarios, protected data, exports, assistant features, and source management.
          </p>
        </section>
      ) : null}
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  readonly label: string;
  readonly value: number;
  readonly tone: 'success' | 'primary' | 'warning' | 'neutral';
}): React.ReactElement {
  const toneClass = {
    success: 'border-emerald-200 bg-emerald-50 text-emerald-950',
    primary: 'border-blue-200 bg-blue-50 text-blue-950',
    warning: 'border-amber-200 bg-amber-50 text-amber-950',
    neutral: 'border-border bg-muted/30 text-foreground',
  }[tone];
  return (
    <div className={`rounded-md border px-3 py-2 ${toneClass}`}>
      <div className="text-xl font-semibold leading-none">{value.toLocaleString()}</div>
      <div className="mt-1 text-[11px] font-medium">{label}</div>
    </div>
  );
}

function humanizeReason(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
